import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingPackages = ({ 
  vendor, 
  onRequestQuote, 
  onBookPackage,
  onToggleComparison,
  isSelectedForComparison = false,
  canAddToComparison = true
}) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showCustomQuote, setShowCustomQuote] = useState(false);

  const handlePackageSelect = (packageId) => {
    setSelectedPackage(packageId);
  };

  const handleBookPackage = (pkg) => {
    onBookPackage?.(pkg);
  };

  const handleRequestQuote = () => {
    onRequestQuote?.(selectedPackage);
  };

  const handleToggleComparison = () => {
    onToggleComparison?.(vendor);
  };

  return (
    <div className="bg-white rounded-xl border border-border shadow-soft">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-heading font-semibold text-xl text-text-primary">
              Pricing Packages
            </h3>
            <p className="text-sm text-text-secondary mt-1">
              Compare this vendor with others to find the best fit
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {/* Compare Button */}
            <Button
              variant={isSelectedForComparison ? "secondary" : "outline"}
              size="sm"
              iconName="BarChart3"
              iconSize={16}
              onClick={handleToggleComparison}
              disabled={!canAddToComparison && !isSelectedForComparison}
            >
              {isSelectedForComparison ? 'Added to Compare' : 'Add to Compare'}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              iconName="Calculator"
              iconSize={16}
              onClick={() => setShowCustomQuote(true)}
            >
              Custom Quote
            </Button>
          </div>
        </div>

        {/* Package Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {vendor.packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative border-2 rounded-xl p-6 transition-smooth cursor-pointer ${
                selectedPackage === pkg.id
                  ? 'border-primary bg-primary-50'
                  : pkg.isPopular
                  ? 'border-secondary bg-secondary-50' :'border-border hover:border-border-dark'
              }`}
              onClick={() => handlePackageSelect(pkg.id)}
            >
              {/* Popular Badge */}
              {pkg.isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </div>
              )}

              {/* Package Header */}
              <div className="text-center mb-6">
                <h4 className="font-heading font-bold text-xl text-text-primary mb-2">
                  {pkg.name}
                </h4>
                <div className="font-heading font-bold text-3xl text-primary mb-1">
                  ${pkg.price.toLocaleString()}
                </div>
                <div className="text-sm text-text-secondary font-caption">
                  {pkg.duration} • Up to {pkg.guestCount} guests
                </div>
              </div>

              {/* Package Features */}
              <div className="space-y-3 mb-6">
                {pkg.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Icon 
                      name={feature.included ? "Check" : "X"} 
                      size={16} 
                      color={feature.included ? "#228B22" : "#B22222"} 
                      className="mt-0.5 flex-shrink-0"
                    />
                    <span className={`text-sm ${
                      feature.included ? 'text-text-primary' : 'text-text-muted line-through'
                    } font-caption`}>
                      {feature.name}
                    </span>
                  </div>
                ))}
              </div>

              {/* Cultural Specializations */}
              {pkg.culturalSpecializations && (
                <div className="mb-6">
                  <div className="text-sm font-medium text-text-primary mb-2">
                    Cultural Expertise:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {pkg.culturalSpecializations.map((culture) => (
                      <span
                        key={culture}
                        className="px-2 py-1 bg-primary-50 text-primary text-xs rounded-full"
                      >
                        {culture}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Package Actions */}
              <div className="space-y-2">
                <Button
                  variant={selectedPackage === pkg.id ? "primary" : "outline"}
                  size="md"
                  fullWidth
                  iconName="BookOpen"
                  iconSize={16}
                  onClick={() => handleBookPackage(pkg)}
                >
                  {selectedPackage === pkg.id ? 'Book This Package' : 'Select Package'}
                </Button>
                
                <div className="text-center">
                  <button className="text-sm text-text-secondary hover:text-primary transition-smooth font-caption">
                    View Package Details
                  </button>
                </div>
              </div>

              {/* Additional Info */}
              <div className="mt-4 pt-4 border-t border-border-light">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary font-caption">Advance Booking:</span>
                  <span className="text-text-primary font-medium">{pkg.advanceBooking}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-text-secondary font-caption">Travel Charges:</span>
                  <span className="text-text-primary font-medium">{pkg.travelCharges}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Package Comparison */}
        <div className="bg-surface-50 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Info" size={16} color="#6B5B4F" />
            <span className="font-medium text-text-primary">Package Information</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-medium text-text-primary mb-2">What's Included:</div>
              <ul className="space-y-1 text-text-secondary font-caption">
                <li>• Professional equipment and setup</li>
                <li>• Cultural music and traditional songs</li>
                <li>• Coordination with other vendors</li>
                <li>• Basic lighting and sound system</li>
              </ul>
            </div>
            <div>
              <div className="font-medium text-text-primary mb-2">Additional Services:</div>
              <ul className="space-y-1 text-text-secondary font-caption">
                <li>• Extended hours: $200/hour</li>
                <li>• Additional equipment: $150-$300</li>
                <li>• Travel beyond 25 miles: $2/mile</li>
                <li>• Rush booking (under 30 days): 20% surcharge</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Custom Quote Section */}
        {showCustomQuote && (
          <div className="bg-primary-50 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-heading font-semibold text-lg text-primary">
                Request Custom Quote
              </h4>
              <button
                onClick={() => setShowCustomQuote(false)}
                className="text-text-secondary hover:text-text-primary transition-smooth"
              >
                <Icon name="X" size={20} color="currentColor" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Event Type
                </label>
                <select className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary">
                  <option>Select event type</option>
                  <option>Sangeet</option>
                  <option>Mehndi</option>
                  <option>Haldi</option>
                  <option>Wedding</option>
                  <option>Reception</option>
                  <option>Multiple Events</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Guest Count
                </label>
                <select className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary">
                  <option>Select guest count</option>
                  <option>Under 100</option>
                  <option>100-200</option>
                  <option>200-300</option>
                  <option>300-500</option>
                  <option>500+</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Event Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Budget Range
                </label>
                <select className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary">
                  <option>Select budget range</option>
                  <option>Under $2,000</option>
                  <option>$2,000 - $5,000</option>
                  <option>$5,000 - $10,000</option>
                  <option>$10,000+</option>
                </select>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Special Requirements
              </label>
              <textarea
                rows={3}
                placeholder="Tell us about your specific needs, cultural requirements, or special requests..."
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
              />
            </div>
            
            <Button
              variant="primary"
              size="md"
              iconName="Send"
              iconSize={16}
              onClick={handleRequestQuote}
            >
              Request Custom Quote
            </Button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="md"
            iconName="MessageCircle"
            iconSize={16}
            fullWidth
          >
            Discuss Pricing
          </Button>
          <Button
            variant="secondary"
            size="md"
            iconName="Download"
            iconSize={16}
            fullWidth
          >
            Download Brochure
          </Button>
          <Button
            variant="primary"
            size="md"
            iconName="Calendar"
            iconSize={16}
            fullWidth
            disabled={!selectedPackage}
          >
            Book Consultation
          </Button>
        </div>

        {/* Payment & Booking Info */}
        <div className="mt-6 p-4 bg-surface-50 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Shield" size={16} color="#228B22" className="mt-0.5" />
            <div className="text-sm text-text-secondary font-caption">
              <div className="font-medium text-text-primary mb-1">Secure Booking Process:</div>
              <ul className="space-y-1">
                <li>• 25% deposit required to secure your date</li>
                <li>• Remaining balance due 7 days before event</li>
                <li>• Full refund if cancelled 30+ days in advance</li>
                <li>• All payments processed securely through ShaadiNest</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPackages;